import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { Bell } from "lucide-react";
import { Button } from "./ui/button";
import { UserAvatar } from "./UserAvatar";
import axios from "axios";
import { useTranslation } from "../i18n";

const API = '/api';

function timeAgo(dateStr, t) {
  const now = new Date();
  const d = new Date(dateStr);
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return t.feed.timeAgo.now;
  if (diff < 3600) return t.feed.timeAgo.minutes(Math.floor(diff / 60));
  if (diff < 86400) return t.feed.timeAgo.hours(Math.floor(diff / 3600));
  return t.feed.timeAgo.days(Math.floor(diff / 86400));
}

export function NotificationBell() {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const ref = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => { fetchNotifications(); const id = setInterval(fetchNotifications, 30000); return () => clearInterval(id); }, []);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const computePosition = useCallback(() => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const dropdownWidth = 320;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const maxHeight = 384;

    // Horizontal: try right-align first, then left-align, then center
    let left;
    if (rect.right - dropdownWidth >= 8) {
      left = rect.right - dropdownWidth;
    } else if (rect.left + dropdownWidth <= viewportWidth - 8) {
      left = rect.left;
    } else {
      left = Math.max(8, (viewportWidth - dropdownWidth) / 2);
    }

    // Vertical: prefer below, but if no room, show above
    let top;
    if (rect.bottom + maxHeight + 8 <= viewportHeight) {
      top = rect.bottom + 4;
    } else {
      top = Math.max(8, rect.top - maxHeight - 4);
    }

    setDropdownStyle({ position: "fixed", top, left, width: dropdownWidth, zIndex: 9999 });
  }, []);

  const handleToggle = () => {
    const next = !open;
    if (next) {
      computePosition();
      if (unread > 0) markAllRead();
    }
    setOpen(next);
  };

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/notifications?limit=10`);
      setNotifications(res.data.notifications || []);
      setUnread(res.data.unread || 0);
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {});
      setUnread(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch {}
  };

  return (
    <div className="relative" ref={ref}>
      <Button ref={btnRef} variant="ghost" size="icon" onClick={handleToggle} className="relative" data-testid="notification-bell">
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-destructive text-destructive-foreground text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="notification-badge">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </Button>
      {open && (
        <div style={dropdownStyle} className="max-h-96 bg-card border border-border rounded-lg shadow-lg overflow-hidden" data-testid="notification-dropdown">
          <div className="p-3 border-b border-border flex items-center justify-between">
            <span className="text-sm font-medium">{t.notifications.title}</span>
            {notifications.length > 0 && (
              <button className="text-xs text-primary hover:underline" onClick={markAllRead}>{t.notifications.markAllRead}</button>
            )}
          </div>
          <div className="overflow-y-auto max-h-72">
            {notifications.length === 0 ? (
              <p className="text-center text-sm text-muted-foreground py-6">{t.notifications.empty}</p>
            ) : (
              notifications.map(n => (
                <Link
                  key={n.notification_id}
                  to={n.from_user?.username ? `/profile/${n.from_user.username}` : "#"}
                  onClick={() => setOpen(false)}
                  className={`flex items-center gap-3 p-3 border-b border-border/50 hover:bg-muted/50 transition-colors ${!n.read ? "bg-primary/5" : ""}`}
                  data-testid={`notification-${n.notification_id}`}
                >
                  <UserAvatar user={n.from_user} size="sm" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm leading-tight">{n.message}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">{timeAgo(n.created_at, t)}</p>
                  </div>
                  {!n.read && <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />}
                </Link>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
